// An LO's business with LendSure, shown on the LO record and as one quiet line in the LO list
// ("linked, not mixed": owner-approved concept 6, 2026-10-25). Counts, stage chips and the closed
// total only; no loan amounts, rates, prices or file facts. The server
// (`GET /api/prospecting/people/{id}/business`, apps/pipeline_api/src/crm/business.rs) counts only
// loans the viewer can already see in Active deals, linked to the LO through Broker links.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { money } from "./format";
import type { Stage } from "./types";

export interface StageCount {
  /** A stage key from the app's stage list, or null when the loan has no stage set. */
  stage_key: string | null;
  count: number;
}

export interface LoBusiness {
  person_id: string;
  /** null: not tracked in the staff app yet. */
  scenarios: number | null;
  /** null: not tracked in the staff app yet. */
  pq_requests: number | null;
  active: number;
  active_by_stage: StageCount[];
  closed: { units: number; volume: string; units_without_amount: number };
  period: "all_time";
}

export interface LoBusinessBrief {
  person_id: string;
  active: number;
  closed_units: number;
}

/** The stage the pipeline treats as closed (funded) business: `loan_stage` key in migration 001. */
export const FUNDED_STAGE = "funded";
/** The Active deals filter value for loans with no stage set. */
export const NO_STAGE = "none";

export const NOT_TRACKED = "Not tracked yet";

export const businessKeys = {
  person: (id: string) => ["crm", "business", id] as const,
  briefs: ["crm", "business"] as const,
};

export function useLoBusiness(personId: string, enabled: boolean) {
  return useQuery({
    queryKey: businessKeys.person(personId),
    queryFn: () => api<LoBusiness>(`/api/prospecting/people/${personId}/business`),
    enabled,
    retry: false,
  });
}

export function useLoBusinessBriefs(enabled: boolean) {
  return useQuery({
    queryKey: businessKeys.briefs,
    queryFn: () => api<LoBusinessBrief[]>("/api/prospecting/business"),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** The LO list's one quiet line, or null when the LO has no business with us. */
export function businessLine(b: LoBusinessBrief | undefined): string | null {
  if (!b) return null;
  const parts: string[] = [];
  if (b.active > 0) parts.push(plural(b.active, "active deal", "active deals"));
  if (b.closed_units > 0) parts.push(b.active > 0 ? `${b.closed_units} closed` : plural(b.closed_units, "closed deal", "closed deals"));
  return parts.length ? parts.join(" · ") : null;
}

/** Active deals (the pipeline) filtered to this LO; optionally one stage, or open loans only. */
export function activeDealsHref(personId: string, filter: { stage?: string | null; open?: boolean } = {}): string {
  const p = new URLSearchParams({ lo: personId, month: "all" });
  if (filter.stage !== undefined) p.set("stage", filter.stage ?? NO_STAGE);
  if (filter.open) p.set("open", "1");
  return `/pipeline?${p}`;
}

/** The Scenarios workspace filtered to this LO's linked scenarios. */
export function scenariosHref(personId: string): string {
  return `/scenarios?${new URLSearchParams({ lo: personId })}`;
}

export interface StageChip {
  key: string;
  /** The app's stage, or undefined for "stage not set". */
  stage: Stage | undefined;
  label: string;
  count: number;
  href: string;
}

/**
 * One chip per stage that has active deals, from the app's stage list. A key the stage list does not
 * know is never given a made-up name: it joins "Stage not set".
 */
export function stageChips(b: LoBusiness, stages: Stage[]): StageChip[] {
  const byKey = new Map(stages.map((s) => [s.key, s]));
  const chips: StageChip[] = [];
  let unset = 0;
  for (const s of b.active_by_stage) {
    const stage = s.stage_key ? byKey.get(s.stage_key) : undefined;
    if (!stage) {
      unset += s.count;
      continue;
    }
    chips.push({ key: stage.key, stage, label: stage.label, count: s.count, href: activeDealsHref(b.person_id, { stage: stage.key }) });
  }
  chips.sort((a, b2) => (a.stage?.sort ?? 99) - (b2.stage?.sort ?? 99));
  if (unset > 0) chips.push({ key: NO_STAGE, stage: undefined, label: "Stage not set", count: unset, href: activeDealsHref(b.person_id, { stage: null }) });
  return chips;
}

/** "Closed: 3 units · $650,000", or null when nothing has closed. */
export function closedText(closed: LoBusiness["closed"]): string | null {
  if (closed.units === 0) return null;
  return `Closed: ${plural(closed.units, "unit", "units")} · ${money(closed.volume) || "$0"}`;
}

/** The note for closed units whose amount is not recorded (they are not in the dollar total). */
export function closedGap(closed: LoBusiness["closed"]): string | null {
  const n = closed.units_without_amount;
  if (n === 0) return null;
  return `${plural(n, "unit has", "units have")} no loan amount recorded`;
}

export interface BusinessTile {
  key: "scenarios" | "pq_requests" | "active";
  label: string;
  /** The count, or the "not tracked" text. */
  value: string;
  tracked: boolean;
  /** Where the tile opens (its own section, filtered to this LO); null when there is nothing to open. */
  href: string | null;
}

export function businessTiles(b: LoBusiness): BusinessTile[] {
  const untracked = (key: "scenarios" | "pq_requests", label: string, n: number | null): BusinessTile =>
    n === null ? { key, label, value: NOT_TRACKED, tracked: false, href: null } : { key, label, value: String(n), tracked: true, href: null };
  const scenarios = untracked("scenarios", "Scenarios", b.scenarios);
  if (b.scenarios !== null) scenarios.href = b.scenarios > 0 ? scenariosHref(b.person_id) : null;
  return [
    scenarios,
    untracked("pq_requests", "PQ requests", b.pq_requests),
    { key: "active", label: "Active deals", value: String(b.active), tracked: true, href: b.active > 0 ? activeDealsHref(b.person_id, { open: true }) : null },
  ];
}

export const CLEAR_LO_TO_ADD = "Clear the LO filter to add a loan";

export interface PipelineView {
  heading: string;
  showOwner: boolean;
  canCreate: boolean;
  /** Why "New loan" is missing, when the view hides it. */
  createHint: string | null;
  /** Whether the summary compares PIN volume with the viewer's plan. */
  showPlan: boolean;
  /** The summary's label for the loans in view. */
  summaryLabel: string;
}

export const LO_SUMMARY_LABEL = "Linked to this LO";

/**
 * Active deals opened from an LO record (`?lo=`) lists every loan linked to that LO that the viewer
 * can see, colleagues' included, whatever the scope. So the heading names the LO instead of the
 * scope, the Owner column shows, and "New loan" is hidden: a new loan is not linked to the LO and
 * would never appear in this view. The summary keeps plain volume and counts for the LO's loans but
 * drops PIN against plan: the plan is the viewer's own, the loans are not.
 */
export function pipelineView(base: Pick<PipelineView, "heading" | "showOwner" | "canCreate">, lo: string | null, loName: string | null): PipelineView {
  if (!lo) return { ...base, createHint: null, showPlan: true, summaryLabel: "In view" };
  return {
    heading: loName ? `Deals with ${loName}` : "Deals with this LO",
    showOwner: true,
    canCreate: false,
    createHint: base.canCreate ? CLEAR_LO_TO_ADD : null,
    showPlan: false,
    summaryLabel: LO_SUMMARY_LABEL,
  };
}
